// mu — task error classes.
//
// Every task verb that can fail in a typed way has its own error class
// here. The CLI's `classifyError()` (src/cli.ts) maps them to exit codes:
//   not found  → 3   (TaskNotFoundError)
//   conflict   → 4   (TaskExistsError, TaskNotInWorkstreamError,
//                     TaskAlreadyOwnedError, ClaimerNotRegisteredError,
//                     CrossWorkstreamEdgeError,
//                     SubstateReasonRequiredError, TaskParkStateError,
//                     TaskParkedError, TaskInTriageError,
//                     InvalidSubstateError)
//   cycle      → 4   (CycleError — also a conflict)
//   usage      → 2   (TaskIdInvalidError; TaskTitleSlugEmptyError in id.ts)
//
// Each error implements HasNextSteps so the CLI can render a per-error
// `Next:` block with the most useful follow-up commands.
//
// Extracted from src/tasks.ts as part of refactor_split_large_src_files.

import type { HasNextSteps, NextStep } from "../output.js";
import { WORKSPACE_STALE_THRESHOLD, type WorkspaceStaleness } from "../workspace.js";
import { sanitiseTaskId } from "./id.js";
import { TASK_SUBSTATES, type TaskStatus, type TaskSubstate } from "./status.js";

export class TaskNotFoundError extends Error implements HasNextSteps {
  override readonly name = "TaskNotFoundError";
  constructor(public readonly taskId: string) {
    super(`no such task: ${taskId}`);
  }
  errorNextSteps(): NextStep[] {
    // v5: tasks.workstream (TEXT) was replaced by tasks.workstream_id
    // (FK → workstreams.id). The pre-v5 recipe SELECTed a column that
    // no longer exists and crashed at runtime — and this is THE first
    // hint a user sees on a missed-task lookup, so the breakage was
    // high-traffic. Mirror the join pattern used by AgentExistsError.
    const idLit = this.taskId.replace(/'/g, "''").toLowerCase();
    const recipe = `mu sql "SELECT ws.name AS workstream, t.local_id, t.status, t.title FROM tasks t JOIN workstreams ws ON ws.id = t.workstream_id WHERE LOWER(t.local_id) LIKE '%${idLit}%' OR LOWER(t.title) LIKE '%${idLit}%'"`;
    return [
      { intent: "List tasks in workstream", command: "mu task list -w <workstream>" },
      { intent: "Search every workstream by substring (id + title)", command: recipe },
    ];
  }
}

/**
 * Thrown by `addTask` when `localId` violates the schema regex
 * `/^[a-z][a-z0-9_-]{0,63}$/`. Replaces a bare `TypeError` so the
 * CLI's `handle()` wrapper can map it to exit code 2 (usage, with the
 * verb's --help, like WorkstreamNameInvalidError) and surface a `--json` `nextSteps` block pointing at
 * the auto-derived-id workflow and a sanitised candidate.
 */
export class TaskIdInvalidError extends Error implements HasNextSteps {
  override readonly name = "TaskIdInvalidError";
  constructor(public readonly attempted: string) {
    super(`invalid task id: ${JSON.stringify(attempted)} (expected /^[a-z][a-z0-9_-]{0,63}$/)`);
  }
  errorNextSteps(): NextStep[] {
    const sanitised = sanitiseTaskId(this.attempted);
    return [
      {
        intent: "Use the auto-derived id (drop the <id> positional; --title derives it)",
        command: 'mu task add --title "..." --impact <n> --effort-days <n>',
      },
      {
        intent: "Sanitise to a valid id",
        command: `mu task add ${sanitised} --title "..." --impact <n> --effort-days <n>`,
      },
    ];
  }
}

export class TaskExistsError extends Error implements HasNextSteps {
  override readonly name = "TaskExistsError";
  constructor(public readonly taskId: string) {
    super(`task already exists: ${taskId}`);
  }
  errorNextSteps(): NextStep[] {
    return [
      { intent: "Show the existing task", command: `mu task show ${this.taskId}` },
      {
        intent: "Update fields on the existing task",
        command: `mu task update ${this.taskId} --title "..." --impact <n> --effort-days <n>`,
      },
      {
        intent: "Pick a different id",
        command: 'mu task add <new-id> --title "..." --impact <n> --effort-days <n>',
      },
    ];
  }
}

/**
 * Thrown when a verb is invoked with `-w/--workstream <name>` but the
 * named task lives in a different workstream. Distinguishes "the user
 * typo'd the workstream" from "the task doesn't exist anywhere"
 * (which surfaces as `TaskNotFoundError`). Maps to exit code 4
 * (conflict / wrong scope).
 */
export class TaskNotInWorkstreamError extends Error implements HasNextSteps {
  override readonly name = "TaskNotInWorkstreamError";
  constructor(
    public readonly taskId: string,
    public readonly expectedWorkstream: string,
    public readonly actualWorkstream: string,
  ) {
    super(`task ${taskId} is in workstream ${actualWorkstream}, not ${expectedWorkstream}`);
  }
  errorNextSteps(): NextStep[] {
    return [
      {
        intent: "Use the correct workstream",
        command: `mu task show ${this.taskId} -w ${this.actualWorkstream}`,
      },
      {
        intent: "List tasks in the requested workstream",
        command: `mu task list -w ${this.expectedWorkstream}`,
      },
    ];
  }
}

export class TaskAlreadyOwnedError extends Error implements HasNextSteps {
  override readonly name = "TaskAlreadyOwnedError";
  constructor(
    public readonly taskId: string,
    public readonly currentOwner: string,
  ) {
    super(`task ${taskId} is already owned by ${currentOwner}`);
  }
  errorNextSteps(): NextStep[] {
    return [
      {
        intent: "See the current owner's task list",
        command: `mu task owned-by ${this.currentOwner}`,
      },
      {
        intent: "Release the current claim (if you ARE the owner)",
        command: `mu task release ${this.taskId}`,
      },
      { intent: "Show full task state", command: `mu task show ${this.taskId}` },
    ];
  }
}

/**
 * Thrown when `mu task claim` resolves a claimer agent name (from the
 * pane title or --for) that has no matching row in the agents table.
 *
 * The FK on `tasks.owner_id` references `agents.id`; without this guard
 * the claim attempt would fail with the unhelpful 'FOREIGN KEY constraint
 * failed' from SQLite. This typed error gives the user actionable next
 * steps (run `mu agent adopt <pane-id>` to register, or use --for to pick a
 * different agent).
 *
 * Maps to exit code 4 (conflict) via the cli.ts handler.
 */
export class ClaimerNotRegisteredError extends Error implements HasNextSteps {
  override readonly name = "ClaimerNotRegisteredError";
  constructor(
    public readonly agentName: string,
    public readonly paneId: string | null,
  ) {
    const paneHint = paneId !== null ? ` (pane ${paneId})` : "";
    super(
      `claimer '${agentName}'${paneHint} is not a registered mu agent (no row in agents table)`,
    );
  }

  /**
   * Three actionable resolutions in expected-frequency order:
   *   1. --self  : orchestrator pattern (working directly)
   *   2. --for   : dispatcher pattern (assigning to a worker)
   *   3. mu agent adopt: registration pattern (promote pane to worker)
   */
  errorNextSteps(): NextStep[] {
    const steps: NextStep[] = [
      { intent: "Work directly (anonymous)", command: "mu task claim <id> --self" },
      { intent: "Dispatch to a worker", command: "mu task claim <id> --for <worker>" },
    ];
    steps.push(
      this.paneId !== null
        ? { intent: "Register this pane", command: `mu agent adopt ${this.paneId}` }
        : {
            intent: "Register a pane",
            command: "mu agent adopt <pane-id>  (must be in mu-<workstream> tmux session)",
          },
    );
    return steps;
  }
}

export class TaskClaimStaleWorkspaceError extends Error implements HasNextSteps {
  override readonly name = "TaskClaimStaleWorkspaceError";
  constructor(public readonly staleness: WorkspaceStaleness) {
    super(
      `${staleness.agentName} workspace is ${staleness.commitsBehindMain} commits behind main (≥${WORKSPACE_STALE_THRESHOLD} = stale); refresh before dispatch or rerun without --strict-staleness`,
    );
  }
  errorNextSteps(): NextStep[] {
    return [
      {
        intent: "Refresh first",
        command: `mu workspace refresh ${this.staleness.agentName} -w ${this.staleness.workstreamName}`,
      },
    ];
  }
}

export class CycleError extends Error implements HasNextSteps {
  override readonly name = "CycleError";
  constructor(
    public readonly from: string,
    public readonly to: string,
    /** Existing path `to -> … -> from` (task names) that the new edge would close. */
    public readonly path: readonly string[] = [],
    public readonly workstream?: string,
  ) {
    super(
      path.length > 1
        ? `adding edge ${from} -> ${to} would create a cycle: ${[from, ...path].join(" -> ")}`
        : `adding edge ${from} -> ${to} would create a cycle`,
    );
  }
  errorNextSteps(): NextStep[] {
    const a = this.path[0];
    const b = this.path[1];
    const ws = this.workstream ? ` -w ${this.workstream}` : "";
    const concrete: NextStep[] =
      a !== undefined && b !== undefined
        ? [
            {
              intent: `Break the cycle by removing existing edge ${a} -> ${b}`,
              command: `mu task unblock ${b} --by ${a}${ws}`,
            },
          ]
        : [];
    return [
      ...concrete,
      {
        intent: "Show the dependency tree",
        command: `mu task tree ${this.to} --down`,
      },
      {
        intent: "Show the prereq tree (what blocks the from-task)",
        command: `mu task tree ${this.from}`,
      },
      {
        intent: "Remove an edge in the path to break the cycle",
        command: "mu task unblock <blocked> --by <blocker>",
      },
    ];
  }
}

/**
 * Thrown by the `mu task wait` CLI wrapper when the per-poll
 * reconciler detects that a watched task transitioned
 * `IN_PROGRESS → OPEN` between polls (the reaper saw the owner's
 * pane was gone and flipped the task back). With `--status CLOSED`
 * (the default) the wait can never satisfy by progress — the worker
 * is dead — so we abort fast instead of running out the operator's
 * `--timeout`.
 *
 * Maps to exit code 6 (REAPER_DETECTED) via the cli.ts handler. The
 * suppression rule (only fire when target=CLOSED) lives in the
 * caller; this error type is a pure data carrier.
 *
 * Surfaced live by task_wait_reconcile_dead_panes (twice in one
 * v0.3 dispatch wave: tmux restart killed worker panes; `mu task
 * wait --timeout 1800` blocked silently for 25 min instead of
 * failing in seconds).
 */
export class ReaperDetectedDuringWaitError extends Error implements HasNextSteps {
  override readonly name = "ReaperDetectedDuringWaitError";
  constructor(
    public readonly taskId: string,
    public readonly previousOwner: string | null,
    public readonly workstream: string,
  ) {
    const ownerBit = previousOwner !== null ? `owner=${previousOwner}` : "owner=<unknown>";
    super(
      `task ${taskId} was IN_PROGRESS ${ownerBit} until just now; reaper detected dead pane and flipped to OPEN. wait abandoned. Re-dispatch a worker and retry.`,
    );
  }
  errorNextSteps(): NextStep[] {
    const ws = this.workstream;
    return [
      {
        intent: "Inspect the task's current state",
        command: `mu task show ${this.taskId} -w ${ws}`,
      },
      {
        intent: "List live agents in the workstream (post-reap)",
        command: `mu agent list -w ${ws}`,
      },
      {
        intent: "Re-dispatch a fresh worker, then re-run the wait",
        command: `mu agent spawn <name> -w ${ws}  &&  mu task claim ${this.taskId} --for <name> -w ${ws}`,
      },
    ];
  }
}

/**
 * Thrown by the `mu task wait` CLI wrapper when `--on-stall exit` is
 * in effect and the existing `--stuck-after` predicate fires on a
 * watched task — the task is IN_PROGRESS, owned by a registered
 * agent whose detected status is `needs_input` for `>= stuckAfterMs`.
 *
 * Pairs with `ReaperDetectedDuringWaitError` (exit 6, dead pane).
 * Stall is the AMBIGUOUS sibling: the worker is alive but not
 * progressing — the operator decides whether it's transient (poke +
 * retry) or terminal (release + reopen). Exit code 7 = STALL_DETECTED
 * via classifyError, distinct from 6 so consumer scripts can branch.
 *
 * Carve-out (lives at the call site, not here): only fires when the
 * wait target is CLOSED — same logic as exit-6's reaper-flip
 * suppression. With `--status OPEN`/etc the worker reaching
 * needs_input might BE the success path.
 *
 * Surfaced by task_wait_stall_action_flag (the warn-only behaviour
 * pre-dates this; the typed-throw path is the new escape hatch for
 * unattended orchestrators).
 */
/**
 * A stalled pi owner's last answer, read exactly: `--after-runs <runs-1>`
 * returns the last settled run's lastText at once. Empty unless the
 * owner's socket reported at least one settled run. The pane read stays
 * next to it: dialogs and crashes show in the pane, not the session.
 */
export function stallWaitHint(owner: string, ws: string, runs: number | undefined): NextStep[] {
  if (runs === undefined || runs < 1) return [];
  return [
    {
      intent: "Read the worker's last answer (its final text, at once)",
      command: `mu agent wait ${owner} --after-runs ${runs - 1} --json -w ${ws}`,
    },
  ];
}

export class StallDetectedDuringWaitError extends Error implements HasNextSteps {
  override readonly name = "StallDetectedDuringWaitError";
  constructor(
    public readonly taskName: string,
    public readonly owner: string | null,
    public readonly workstream: string,
    public readonly ageSecs: number,
    /** The owner runs pi (control socket): `mu agent abort` stops its turn. */
    public readonly ownerExpectsCtl = false,
    /** The owner's ctl run count at the stall, when its socket answered. */
    public readonly ownerRuns?: number,
    /** The owner's workstream when it differs from the task's
     *  (`task claim --for <ws>/<agent>`). Agent commands use it. */
    public readonly ownerWorkstream: string = workstream,
  ) {
    const ownerBit = owner !== null ? owner : "<unknown>";
    super(
      `task ${taskName} owned by ${ownerBit} has been needs_input for ${ageSecs}s; exiting per --on-stall exit. Read the pane to see which it is — finished without closing, waiting on an answer, or sitting at a prompt.`,
    );
  }
  /**
   * Diagnose before acting. `mu agent read` comes FIRST because the
   * predicate that threw cannot tell the three causes of needs_input
   * apart, and the evidence is in the pane rather than the task row.
   * The earlier order led with "poke the worker", which is the right
   * move for only one of the three and actively wrong for a worker
   * waiting on an answer — a poke with no answer in it just restates
   * the question.
   */
  errorNextSteps(): NextStep[] {
    const ws = this.workstream;
    const ownerWs = this.ownerWorkstream;
    const ownerBit = this.owner !== null ? this.owner : "<owner>";
    return [
      ...stallWaitHint(ownerBit, ownerWs, this.ownerRuns),
      {
        intent: "Read the worker's pane to see what it is waiting on",
        command: `mu agent read ${ownerBit} -w ${ownerWs} --lines 60`,
      },
      {
        intent: "Answer a question, or poke a worker that stalled mid-step",
        command: `mu agent send ${ownerBit} '<answer-or-retry-instruction>' -w ${ownerWs}`,
      },
      ...(this.ownerExpectsCtl
        ? [
            {
              intent: "Stuck mid-turn? Stop it through the control socket before kick or close",
              command: `mu agent abort ${ownerBit} -w ${ownerWs}`,
            },
          ]
        : []),
      {
        intent: "Close the task if the worker finished but skipped it",
        command: `mu task close ${this.taskName} -w ${ws}`,
      },
      {
        intent: "Release the task back to OPEN (declare the stall terminal)",
        command: `mu task release ${this.taskName} --reopen -w ${ws}`,
      },
    ];
  }
}

export class CrossWorkstreamEdgeError extends Error implements HasNextSteps {
  override readonly name = "CrossWorkstreamEdgeError";
  constructor(
    public readonly blocker: string,
    public readonly blockerWorkstream: string,
    public readonly dependent: string,
    public readonly dependentWorkstream: string,
  ) {
    super(
      `cross-workstream edge: blocker '${blocker}' is in workstream '${blockerWorkstream}', dependent '${dependent}' is in workstream '${dependentWorkstream}'`,
    );
  }
  errorNextSteps(): NextStep[] {
    // No "move the blocker" recipe. A task's workstream is part of its
    // natural key (`<ws>/<local_id>`), which every op is filed under, so
    // a raw `UPDATE tasks SET workstream_id=...` re-keys the row with no
    // op for either key: sync, undo and `mu doctor` then disagree with
    // the table. The capture layer refuses that UPDATE (src/capture.ts
    // KEY_COLUMNS). The "rename one workstream to the other" hint went
    // earlier for the same reason, and because it moved every task.
    return [
      {
        intent: "Duplicate the blocker into the dependent's workstream",
        command: `mu task add <new-id> -w ${this.dependentWorkstream} --title "<copy of ${this.blocker}>" --impact <n> --effort-days <n>`,
      },
      {
        intent: "Or wait on the blocker across workstreams instead of an edge",
        command: `mu task wait ${this.blockerWorkstream}/${this.blocker}`,
      },
    ];
  }
}

/**
 * Thrown by `closeTask` (non-`done` `--as`) and `parkTask` when no
 * non-empty `--why` was given. The reason is stored as a note, so a
 * classification with no rationale is refused before any write.
 */
export class SubstateReasonRequiredError extends Error implements HasNextSteps {
  override readonly name = "SubstateReasonRequiredError";
  constructor(
    public readonly verb: "close" | "park",
    public readonly substate: TaskSubstate,
    public readonly taskId: string,
  ) {
    super(
      verb === "close"
        ? `closing ${taskId} as ${substate} needs a reason (--why "...")`
        : `parking ${taskId} needs a reason (--why "...")`,
    );
  }
  errorNextSteps(): NextStep[] {
    const cmd =
      this.verb === "close"
        ? `mu task close ${this.taskId} --as ${this.substate} --why "<reason>"`
        : `mu task park ${this.taskId} --why "<reason>"`;
    return [{ intent: "Retry with a reason", command: cmd }];
  }
}

/**
 * Thrown by `parkTask` on an IN_PROGRESS or CLOSED task. Park is one
 * transition (OPEN/todo → OPEN/parked); the owner releases, or the task
 * is reopened, first.
 */
export class TaskParkStateError extends Error implements HasNextSteps {
  override readonly name = "TaskParkStateError";
  constructor(
    public readonly taskId: string,
    public readonly status: "IN_PROGRESS" | "CLOSED",
    public readonly workstream: string,
  ) {
    super(
      status === "IN_PROGRESS"
        ? `cannot park ${taskId}: it is IN_PROGRESS (release it first)`
        : `cannot park ${taskId}: it is CLOSED (open it first)`,
    );
  }
  errorNextSteps(): NextStep[] {
    const ws = this.workstream;
    const first: NextStep =
      this.status === "IN_PROGRESS"
        ? { intent: "Release the claim first", command: `mu task release ${this.taskId} -w ${ws}` }
        : { intent: "Reopen the task first", command: `mu task open ${this.taskId} -w ${ws}` };
    return [
      first,
      {
        intent: "Then park it",
        command: `mu task park ${this.taskId} --why "<reason>" -w ${ws}`,
      },
    ];
  }
}

/**
 * Thrown by `claimTask` on an OPEN/triage task without `force`. A triage
 * task is a proposal (often a review finding) nobody has accepted as
 * work yet; accept it, or close it rejected / duplicate.
 */
export class TaskInTriageError extends Error implements HasNextSteps {
  override readonly name = "TaskInTriageError";
  constructor(
    public readonly taskId: string,
    public readonly workstream: string,
  ) {
    super(`task ${taskId} is in triage (OPEN/triage); accept it or claim with --force`);
  }
  errorNextSteps(): NextStep[] {
    const ws = this.workstream;
    return [
      {
        intent: "Accept it as work, then claim",
        command: `mu task accept ${this.taskId} -w ${ws}`,
      },
      {
        intent: "Decline it",
        command: `mu task close ${this.taskId} --as rejected --why "<reason>" -w ${ws}`,
      },
      { intent: "Claim it anyway", command: `mu task claim ${this.taskId} --force -w ${ws}` },
    ];
  }
}

/**
 * Thrown by `claimTask` on an OPEN/parked task without `force`. Parked
 * means "keep out of the scheduler"; claiming it anyway is explicit.
 */
export class TaskParkedError extends Error implements HasNextSteps {
  override readonly name = "TaskParkedError";
  constructor(
    public readonly taskId: string,
    public readonly workstream: string,
  ) {
    super(`task ${taskId} is parked (OPEN/parked); unpark it or claim with --force`);
  }
  errorNextSteps(): NextStep[] {
    const ws = this.workstream;
    return [
      { intent: "Unpark it, then claim", command: `mu task unpark ${this.taskId} -w ${ws}` },
      { intent: "Claim it anyway", command: `mu task claim ${this.taskId} --force -w ${ws}` },
    ];
  }
}

/**
 * Thrown when a verb is asked for a (status, substate) pair that the
 * task_substates table does not allow, e.g. `close --as parked`.
 * Caught here rather than by the deferred FK, which would fail only at
 * commit with an opaque constraint message.
 */
export class InvalidSubstateError extends Error implements HasNextSteps {
  override readonly name = "InvalidSubstateError";
  constructor(
    public readonly status: TaskStatus,
    public readonly substate: string,
  ) {
    super(
      `invalid substate for ${status}: ${JSON.stringify(substate)} (expected one of: ${TASK_SUBSTATES[status].join(", ")})`,
    );
  }
  errorNextSteps(): NextStep[] {
    return TASK_SUBSTATES[this.status].map((s) => ({
      intent: `Use ${this.status}/${s}`,
      command:
        this.status === "CLOSED" ? `mu task close <id> --as ${s}` : `mu task list --substate ${s}`,
    }));
  }
}
